// Image Optimization and Lazy Loading Utilities
// Custom Design Tool - Customer Manager

import * as React from 'react';

interface ImageConfig {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  quality?: number;
  format?: 'webp' | 'avif' | 'jpg' | 'png';
  sizes?: string;
  priority?: boolean;
  placeholder?: 'blur' | 'empty' | string;
  className?: string;
  style?: React.CSSProperties;
  onLoad?: () => void;
  onError?: () => void;
}

interface LazyImageOptions {
  rootMargin?: string;
  threshold?: number;
  fadeInDuration?: number;
  placeholder?: string;
}

// WebP and AVIF support detection
class ImageFormatDetector {
  private static supportCache: Map<string, boolean> = new Map();

  static async supportsFormat(format: string): Promise<boolean> {
    if (this.supportCache.has(format)) {
      return this.supportCache.get(format)!;
    }

    return new Promise((resolve) => {
      const img = new Image();
      
      img.onload = () => {
        const supported = img.width > 0 && img.height > 0;
        this.supportCache.set(format, supported);
        resolve(supported);
      };
      
      img.onerror = () => {
        this.supportCache.set(format, false);
        resolve(false);
      };

      // Test images for format support
      const testImages = {
        webp: 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA',
        avif: 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgABogQEAwgMg8f8D///8WfhwB8+ErK42A='
      };

      img.src = testImages[format as keyof typeof testImages] || '';
    });
  }

  static async getBestFormat(): Promise<'avif' | 'webp' | 'jpg'> {
    if (await this.supportsFormat('avif')) {
      return 'avif';
    } else if (await this.supportsFormat('webp')) {
      return 'webp';
    }
    return 'jpg';
  }
}

// Lazy loading implementation
class LazyImageLoader {
  private static observer: IntersectionObserver | null = null;
  private static defaultOptions: LazyImageOptions = {
    rootMargin: '50px',
    threshold: 0.01,
    fadeInDuration: 300,
    placeholder: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+CiAgPHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzlCA0EzQUYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuMzVlbSI+TG9hZGluZy4uLjwvdGV4dD4KICA8L3N2Zz4K'
  };

  static init(options: LazyImageOptions = {}): void {
    const config = { ...this.defaultOptions, ...options };
    
    if (!this.observer && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target as HTMLImageElement;
            this.loadImage(img, config);
            this.observer?.unobserve(img);
          }
        });
      }, {
        rootMargin: config.rootMargin,
        threshold: config.threshold
      });
    }
  }

  static observe(img: HTMLImageElement, options?: LazyImageOptions): void {
    if (!this.observer) {
      this.init(options);
    }

    if (this.observer) {
      this.observer.observe(img);
    } else {
      // Fallback for browsers without IntersectionObserver
      setTimeout(() => this.loadImage(img, { ...this.defaultOptions, ...options }), 100);
    }
  }

  static unobserve(img: HTMLImageElement): void {
    if (this.observer) {
      this.observer.unobserve(img);
    }
  }

  private static async loadImage(img: HTMLImageElement, options: LazyImageOptions): Promise<void> {
    const src = img.dataset.src;
    const srcset = img.dataset.srcset;
    
    if (!src) return;

    try {
      // Create new image to preload
      const imageLoader = new Image();
      
      imageLoader.onload = () => {
        // Update the actual image
        img.src = src;
        if (srcset) img.srcset = srcset;
        
        // Add fade-in effect
        if (options.fadeInDuration && options.fadeInDuration > 0) {
          img.style.opacity = '0';
          img.style.transition = `opacity ${options.fadeInDuration}ms ease-in-out`;
          
          // Trigger fade-in
          requestAnimationFrame(() => {
            img.style.opacity = '1';
          });
        }
        
        // Remove placeholder
        img.classList.remove('lazy-loading');
        img.classList.add('lazy-loaded');
        
        // Trigger onLoad callback if provided
        const onLoad = img.dataset.onload;
        if (onLoad) {
          try {
            new Function(onLoad)();
          } catch (e) {
            console.warn('Error executing onLoad callback:', e);
          }
        }
      };

      imageLoader.onerror = () => {
        // Fallback to error placeholder or original image
        img.classList.add('lazy-error');
        
        const onError = img.dataset.onerror;
        if (onError) {
          try {
            new Function(onError)();
          } catch (e) {
            console.warn('Error executing onError callback:', e);
          }
        }
      };

      // Start loading
      if (srcset) imageLoader.srcset = srcset;
      imageLoader.src = src;

    } catch (error) {
      console.error('Error loading image:', error);
      img.classList.add('lazy-error');
    }
  }

  static disconnect(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}

// Image URL optimization
export class ImageOptimizer {
  private static baseUrl = '/api/images/optimize';

  /**
   * Generate optimized image URL
   */
  static optimizeUrl(src: string, options: Partial<ImageConfig> = {}): string {
    // If it's already an optimized URL or external URL, return as-is
    if (src.includes('http') || src.includes(this.baseUrl)) {
      return src;
    }

    const params = new URLSearchParams();
    
    // Add source
    params.append('src', src);
    
    // Add dimensions
    if (options.width) params.append('w', options.width.toString());
    if (options.height) params.append('h', options.height.toString());
    
    // Add quality
    if (options.quality) params.append('q', options.quality.toString());
    
    // Add format
    if (options.format) params.append('f', options.format);
    
    return `${this.baseUrl}?${params.toString()}`;
  }

  /**
   * Generate responsive image srcset
   */
  static generateSrcSet(src: string, widths: number[], options: Partial<ImageConfig> = {}): string {
    return widths
      .map(width => {
        const url = this.optimizeUrl(src, { ...options, width });
        return `${url} ${width}w`;
      })
      .join(', ');
  }

  /**
   * Generate sizes attribute for responsive images
   */
  static generateSizes(breakpoints: Array<{ maxWidth?: number; size: string }>): string {
    return breakpoints
      .map(bp => bp.maxWidth ? `(max-width: ${bp.maxWidth}px) ${bp.size}` : bp.size)
      .join(', ');
  }

  /**
   * Preload critical images
   */
  static preload(src: string, options: Partial<ImageConfig> = {}): void {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = this.optimizeUrl(src, options);
    
    if (options.sizes) {
      link.setAttribute('imagesizes', options.sizes);
    }
    
    document.head.appendChild(link);
  }

  /**
   * Generate blur placeholder
   */
  static async generateBlurPlaceholder(src: string): Promise<string> {
    try {
      // Use a tiny version for blur effect
      const tinyUrl = this.optimizeUrl(src, { width: 10, quality: 10 });
      
      const response = await fetch(tinyUrl);
      const blob = await response.blob();
      
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.warn('Failed to generate blur placeholder:', error);
      return LazyImageLoader['defaultOptions'].placeholder!;
    }
  }

  /**
   * Check if image exists
   */
  static async checkImageExists(src: string): Promise<boolean> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = src;
    });
  }
}

// React hook for lazy loading
export function useLazyImage(src: string, options: LazyImageOptions = {}): {
  imgRef: React.RefObject<HTMLImageElement>;
  loaded: boolean;
} {
  const imgRef = React.useRef<HTMLImageElement>(null);
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    // Set up data attributes
    img.dataset.src = src;
    img.dataset.onload = 'setLoaded(true)';
    
    // Add to global scope temporarily for callback
    (window as any).setLoaded = setLoaded;

    // Start observing
    LazyImageLoader.observe(img, options);

    return () => {
      if (img) {
        LazyImageLoader.unobserve(img);
      }
      delete (window as any).setLoaded;
    };
  }, [src, options]);

  return { imgRef, loaded };
}

// React component for optimized images
interface OptimizedImageProps extends ImageConfig {
  lazy?: boolean;
  responsive?: boolean;
  breakpoints?: number[];
  placeholder?: 'blur' | 'empty' | string;
}

export const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  width,
  height,
  quality = 80,
  format,
  sizes,
  priority = false,
  placeholder = 'empty',
  lazy = true,
  responsive = true,
  breakpoints = [640, 768, 1024, 1280, 1536],
  className = '',
  style,
  onLoad,
  onError,
  ...props
}) => {
  const { imgRef, loaded } = useLazyImage(src, {
    rootMargin: '50px',
    threshold: 0.01,
    fadeInDuration: 300
  });

  React.useEffect(() => {
    // Preload priority images
    if (priority && !lazy) {
      ImageOptimizer.preload(src, { width, height, quality, format });
    }
  }, [src, priority, lazy, width, height, quality, format]);

  // Generate optimized URLs
  const optimizedSrc = React.useMemo(() => 
    ImageOptimizer.optimizeUrl(src, { width, height, quality, format }),
    [src, width, height, quality, format]
  );

  const srcSet = React.useMemo(() => 
    responsive ? ImageOptimizer.generateSrcSet(src, breakpoints, { quality, format }) : undefined,
    [src, responsive, breakpoints, quality, format]
  );

  const responsiveSizes = React.useMemo(() => 
    sizes || (responsive ? ImageOptimizer.generateSizes([
      { maxWidth: 640, size: '100vw' },
      { maxWidth: 1024, size: '50vw' },
      { size: '33vw' }
    ]) : undefined),
    [sizes, responsive]
  );

  // Placeholder handling
  const placeholderSrc = React.useMemo(() => {
    if (placeholder === 'empty') {
      return LazyImageLoader['defaultOptions'].placeholder;
    } else if (placeholder === 'blur') {
      // This would need to be generated server-side or cached
      return LazyImageLoader['defaultOptions'].placeholder;
    }
    return placeholder;
  }, [placeholder]);

  const imgProps = {
    ref: imgRef,
    alt,
    className: `${className} ${lazy ? 'lazy-loading' : ''} ${loaded ? 'lazy-loaded' : ''}`.trim(),
    style: {
      ...style,
      ...(lazy && !loaded ? { opacity: 0 } : {})
    },
    width,
    height,
    onLoad,
    onError,
    ...props
  };

  if (lazy) {
    return (
      <img
        {...imgProps}
        src={placeholderSrc}
        data-src={optimizedSrc}
        data-srcset={srcSet}
        sizes={responsiveSizes}
        loading="lazy"
        decoding="async"
      />
    );
  }

  return (
    <img
      {...imgProps}
      src={optimizedSrc}
      srcSet={srcSet}
      sizes={responsiveSizes}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
    />
  );
};

// Initialize lazy loading
export const initImageOptimization = (options?: LazyImageOptions) => {
  LazyImageLoader.init(options);
};

// Cleanup function
export const cleanupImageOptimization = () => {
  LazyImageLoader.disconnect();
};

// Export utilities
export { ImageFormatDetector, LazyImageLoader };